import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from '@/lib/navigation';
import { useSession } from '@/contexts/SessionContext';
import { switchPersona } from '@/lib/auth';
import {
    clearCrossPersonaCaches,
    resolvePersonaFallback,
    intendedPersonaForFrontend,
} from '@/lib/persona-switch';
import { isRouteAuthorised } from '@/lib/frontends';
import { useToast } from '@/lib/useToast';
import NotAuthorisedPage from '@/pages/not-authorised/page';
import AppSidebar from './AppSidebar';
import MobileTopBar from './MobileTopBar';
import StaffTopBar from './StaffTopBar';

interface FrontendLayoutProps {
    frontend: 'desk' | 'ops' | 'comply' | 'view';
    children: ReactNode;
}

const FRONTEND_LABELS: Record<FrontendLayoutProps['frontend'], string> = {
    desk: 'Septima Desk',
    ops: 'Septima Ops',
    comply: 'Septima Comply',
    view: 'Septima View',
};

export default function FrontendLayout({ frontend, children }: FrontendLayoutProps) {
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const pathname = usePathname();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { pushToast } = useToast();
    const { user, grantedFrontends, refresh, setActivePersona, isHydrating } = useSession();
    const activePersona = user?.activePersona ?? null;
    // `grantedFrontends` is the allowlist filtered to the four staff views
    // (never 'admin'), so recovery can only ever land on a real view route.
    const isGranted = grantedFrontends.includes(frontend);
    // Route authorisation (ADR-0013 / issue #209). The persona this shell runs
    // as for this user (`im-assistant → desk` is the only non-identity case);
    // the section guard below is checked against it + the granted tokens, so
    // the verdict is timing-independent of the optimistic activePersona switch.
    const shellPersona = user ? intendedPersonaForFrontend(frontend, user.personas) : frontend;
    // Authorised when (a) the frontend itself is granted (cross-frontend bar)
    // AND (b) the active persona may see the section the pathname falls under
    // (im-assistant suitability/vulnerability bar; admin-only Administration).
    // Derived from the SAME policy as the sidebar, so visibility and access
    // cannot drift. An unauthorised route renders the 403 page IN PLACE (the
    // URL is kept; no silent redirect) instead of staff chrome.
    const routeAuthorised =
        isGranted && isRouteAuthorised(pathname, shellPersona, user?.personas ?? []);
    // Track the most-recent (frontend, activePersona) we've already
    // requested a switch for, so React's dev-mode double-invoke and a
    // mid-flight `refresh` re-render don't fire duplicate /api/auth/persona
    // calls (and the corresponding duplicate audit rows).
    const lastSwitchRef = useRef<string | null>(null);
    // Frontends the server has denied a switch to this session — never retry
    // them; bounce to a valid persona instead.
    const deniedRef = useRef<Set<string>>(new Set());

    // Auto-close mobile nav when route changes.
    useEffect(() => {
        setMobileNavOpen(false);
    }, [pathname]);

    // Deep-link auth gate (ADR-0012). The `/` index gates auth, but deep-linking
    // straight to e.g. `/desk/portfolio` while unauthenticated would otherwise
    // render staff chrome. Once the session has hydrated and there is no user,
    // bounce to /login. While hydrating or unauthenticated (pre-redirect) we
    // render a minimal loading state below, so unauthenticated chrome never
    // flashes. Ordered BEFORE the persona-switch effect — there is no persona to
    // reconcile until the user is known.
    useEffect(() => {
        if (isHydrating) return;
        if (!user) {
            navigate('/login', { replace: true });
        }
    }, [isHydrating, user, navigate]);

    // Keep the session's activePersona in lock-step with the rendered view
    // (ADR-0004), optimistically (PRD #156). On entering a granted frontend
    // we reflect the switch in the UI immediately, then fire the server-side
    // switch + audit write in the background — the route is usable without
    // waiting on it. A denied switch (lost grant / audit failure) or a
    // deep-link into an ungranted view recovers to a valid persona via the
    // existing toast/error pattern, so the shell never sits on a persona the
    // server will gate every read against.
    useEffect(() => {
        if (!user) return;
        // A deep-link / saved URL into an ungranted frontend renders the 403 page
        // IN PLACE (issue #209) — no silent redirect. We do NOT switch persona or
        // navigate here; the shell never runs as a persona it has no grant for,
        // and the `routeAuthorised` gate below renders NotAuthorisedPage.
        if (!isGranted) return;
        // The active persona this shell should run as for this user — on the Desk
        // shell an `im-assistant`-only user runs as `im-assistant`, NOT the
        // ungranted `desk` (ADR-0013).
        const intendedPersona = intendedPersonaForFrontend(frontend, user.personas);
        if (activePersona === intendedPersona) return;

        if (deniedRef.current.has(frontend)) {
            const fallback = resolvePersonaFallback(activePersona, grantedFrontends, frontend);
            if (fallback && fallback !== frontend) {
                setActivePersona(fallback);
                navigate(`/${fallback}`, { replace: true });
            }
            return;
        }

        const key = `${intendedPersona}:${activePersona ?? 'unset'}`;
        if (lastSwitchRef.current === key) return;
        lastSwitchRef.current = key;

        // Optimistic: reflect the new persona now; reconcile the session after.
        const previousPersona = activePersona;
        setActivePersona(intendedPersona);
        // Cross-persona cache wall (ADR-0004): domain query keys are
        // persona-blind and this switch is an SPA navigation, so a payload
        // cached under the previous persona (e.g. a comply Suitability Record
        // detail carrying the KYC assessmentContent block — ADR-0025 D5)
        // would otherwise render from cache after switching. Fires only on a
        // REAL persona change — this branch is guarded by the
        // activePersona !== intendedPersona check and the lastSwitchRef
        // dedupe above. The session entry is kept (no hydration flash).
        clearCrossPersonaCaches(queryClient);

        void switchPersona(intendedPersona).then((res) => {
            if (res.ok) {
                // Reconcile session state in the background; route already rendered.
                void refresh();
                return;
            }
            // Denied / errored — recover to a persona the server will honour.
            deniedRef.current.add(frontend);
            lastSwitchRef.current = null;
            pushToast({
                kind: 'error',
                message: 'Could not switch view — returning to your previous view.',
            });
            const fallback = resolvePersonaFallback(previousPersona, grantedFrontends, frontend);
            if (fallback && fallback !== frontend) {
                setActivePersona(fallback);
                navigate(`/${fallback}`, { replace: true });
            }
        });
    }, [
        user,
        frontend,
        activePersona,
        isGranted,
        grantedFrontends,
        refresh,
        setActivePersona,
        pushToast,
        navigate,
        queryClient,
    ]);

    // While the session hydrates, or after it resolves to no user (the effect
    // above is mid-redirect to /login), render the loading state rather than the
    // staff shell — matches the `/` index gate's markup (home.tsx).
    if (isHydrating || !user) {
        return (
            <div className="flex-1 flex items-center justify-center text-body text-[rgb(var(--color-text-secondary))]">
                Loading…
            </div>
        );
    }

    // Route authorisation (issue #209). A saved / typed URL the active persona
    // is not authorised for renders the existing 403 page IN PLACE — the URL is
    // kept (no redirect) so the user sees WHY rather than a blank/broken page.
    // The backend is the real boundary (its endpoints 403); this is the UX
    // mirror, derived from the same persona policy as the sidebar.
    if (!routeAuthorised) {
        return <NotAuthorisedPage variant="route" />;
    }

    return (
        <div className="flex flex-col lg:flex-row h-full overflow-hidden">
            <MobileTopBar
                onOpen={() => setMobileNavOpen(true)}
                frontendLabel={FRONTEND_LABELS[frontend]}
            />

            {mobileNavOpen && (
                <button
                    type="button"
                    aria-label="Close navigation menu"
                    onClick={() => setMobileNavOpen(false)}
                    className="lg:hidden fixed inset-0 z-40 bg-[var(--septima-scrim)]"
                />
            )}

            <AppSidebar
                frontend={frontend}
                mobileOpen={mobileNavOpen}
                onMobileClose={() => setMobileNavOpen(false)}
            />

            <main className="septima-workspace flex-1 flex flex-col overflow-hidden">
                <StaffTopBar frontend={frontend} />
                <div className="flex-1 flex flex-col overflow-hidden">{children}</div>
            </main>
        </div>
    );
}
